import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useFacets, useRecipes } from '../api/recipes';
import CookingTime from '../components/CookingTime';
import RecipeThumb from '../components/RecipeThumb';

const SEASONS = [
  { value: '', label: 'Any season' },
  { value: 'spring', label: '🌱 Spring' },
  { value: 'summer', label: '☀️ Summer' },
  { value: 'fall', label: '🍂 Fall' },
  { value: 'winter', label: '❄️ Winter' },
];

/** Hold off querying until typing pauses — the corpus search hits 4,800 rows. */
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export default function Recipes() {
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [season, setSeason] = useState('');
  const [protein, setProtein] = useState('');
  // The alternatives a card prints are the same dinner with one thing
  // changed, so browsing shows the dish the card is named for until asked.
  const [variants, setVariants] = useState(false);
  // Blocked recipes are not hidden from the corpus -- blocking stops a dinner
  // being suggested, not being looked up -- so this narrows to them rather
  // than filtering them out.
  const [blocked, setBlocked] = useState(false);

  const debouncedSearch = useDebounced(search);
  const { data: facets } = useFacets(variants);
  const { data, isLoading } = useRecipes({
    search: debouncedSearch,
    season,
    protein,
    variants,
    blocked,
  });

  return (
    <section>
      <h1>Recipes</h1>
      {facets && <p className="muted">{facets.total.toLocaleString()} cards</p>}

      <div className="filters">
        <input
          className="filters__search"
          type="search"
          placeholder="Search titles and ingredients…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoCapitalize="none"
        />
        <div className="filters__row">
          <select value={season} onChange={(e) => setSeason(e.target.value)}>
            {SEASONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select value={protein} onChange={(e) => setProtein(e.target.value)}>
            <option value="">Any protein</option>
            {facets?.proteins.map((item) => (
              <option key={item.protein} value={item.protein}>
                {item.protein} ({item.count})
              </option>
            ))}
          </select>
          <select
            value={variants ? 'include' : 'base'}
            onChange={(e) => setVariants(e.target.value === 'include')}
          >
            <option value="base">Original recipes</option>
            <option value="include">Include swaps</option>
          </select>
          <select
            value={blocked ? 'blocked' : 'all'}
            onChange={(e) => setBlocked(e.target.value === 'blocked')}
          >
            <option value="all">All recipes</option>
            <option value="blocked">Blocked ({facets?.blocked ?? 0})</option>
          </select>
        </div>
      </div>

      {isLoading && <p className="center-note">Searching…</p>}

      {data && (
        <>
          <p className="muted">{data.count.toLocaleString()} matching</p>
          <ul className="recipe-list">
            {data.results.map((recipe) => (
              <li key={recipe.id}>
                <Link
                  to={`/recipes/${recipe.id}`}
                  state={{ from: location.pathname + location.search }}
                  className="recipe-card"
                >
                  <RecipeThumb id={recipe.id} title={recipe.title} size="md" />
                  <span className="recipe-card__text">
                    <span className="recipe-card__title">{recipe.title}</span>
                    <span className="recipe-card__meta">
                      {recipe.servings} servings
                      {(recipe.total_time_minutes || recipe.prep_time_minutes) && ' · '}
                      <CookingTime recipe={recipe} />
                      {/* Two near-identical titles read as a duplicate
                          otherwise, rather than as a dish and its swap. */}
                      {recipe.swap_label && (
                        <>
                          {' · '}
                          <span className="chip">{recipe.swap_label}</span>
                        </>
                      )}
                      {/* Said here as well as behind the filter, because a
                          blocked card turns up in an ordinary search too. */}
                      {recipe.blocked && (
                        <>
                          {' · '}
                          <span className="chip chip--blocked">Blocked</span>
                        </>
                      )}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          {data.count === 0 && (
            <p className="center-note">
              {blocked
                ? 'Nothing is blocked. Swapping a dinner out is where that happens.'
                : 'Nothing matched those filters.'}
            </p>
          )}
        </>
      )}
    </section>
  );
}
